import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLinks } from '../hooks/useLinks';
import { useDomains } from '../hooks/useDomains';
import supabaseService from '../services/supabaseService';
import { getLinkDomain, buildShortUrl } from '../utils/domainUtils';

const ModernDashboard = () => {
  const { user } = useAuth();
  const { links } = useLinks();
  const { domains } = useDomains();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalLinks: 0,
    totalClicks: 0,
    clicksToday: 0,
    conversionRate: 0
  });
  const [recentLinks, setRecentLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (links) {
      loadDashboardData();
    }
  }, [links]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const userLinks = links || [];
      const totalLinks = userLinks.length;
      const totalClicks = userLinks.reduce((sum, link) => sum + (link.clickCount || 0), 0);

      // Get real analytics data
      let clicksToday = 0;
      try {
        const analyticsData = await supabaseService.getUserAnalytics(null, 1);
        clicksToday = analyticsData.clicksToday || 0;
      } catch (analyticsError) {
        console.warn('⚠️ Failed to load today\'s analytics:', analyticsError);
      }

      const conversionRate = totalLinks > 0 ? (totalClicks / totalLinks) : 0;

      setStats({
        totalLinks,
        totalClicks,
        clicksToday,
        conversionRate
      });

      setRecentLinks(userLinks.slice(0, 5));

    } catch (error) {
      console.error('❌ Dashboard: Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="modern-dashboard">
      {/* Hero Header */}
      <div className="dashboard-hero">
        <div className="hero-content">
          <div className="welcome-section">
            <h1>Welcome back, {user?.firstName || user?.email?.split('@')[0] || 'Admin'}! 👋</h1>
            <p>Here's what's happening with your links today</p>
          </div>

          <div className="hero-actions">
            <button
              className="primary-action"
              onClick={() => navigate('/admin/links')}
            >
              <span className="action-icon">✨</span>
              Create Link
            </button>
            <button
              className="secondary-action"
              onClick={() => navigate('/admin/domains')}
            >
              <span className="action-icon">🌐</span>
              Manage Domains
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">🔗</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalLinks}</div>
              <div className="stat-label">Total Links</div>
              <div className="stat-change">+12% vs last month</div>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon">👆</div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalClicks}</div>
              <div className="stat-label">Total Clicks</div>
              <div className="stat-change">+8.2% vs last month</div>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.clicksToday}</div>
              <div className="stat-label">Clicks Today</div>
              <div className="stat-change">+15.3% vs yesterday</div>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-value">{stats.conversionRate.toFixed(1)}%</div>
              <div className="stat-label">Avg. CTR</div>
              <div className="stat-change">-2.1% vs last month</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Recent Links */}
        <div className="content-section">
          <div className="section-header">
            <h3>📋 Recent Links</h3>
            <button
              className="view-all-btn"
              onClick={() => navigate('/admin/links')}
            >
              View All
            </button>
          </div>

          <div className="recent-links">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading your links...</p>
              </div>
            ) : recentLinks.length > 0 ? (
              <div className="links-list">
                {recentLinks.map((link) => {
                  const domainName = getLinkDomain(link, domains);
                  const shortUrl = buildShortUrl(link.shortCode, domainName);

                  return (
                    <div key={link.id} className="recent-link-item">
                      <div className="link-thumbnail">
                        {link.imageUrl ? (
                          <img src={link.imageUrl} alt="Link preview" />
                        ) : (
                          <div className="thumbnail-placeholder">🔗</div>
                        )}
                      </div>

                      <div className="link-info">
                        <h4 className="link-title">{link.title || 'Untitled Link'}</h4>
                        <div className="link-url">{shortUrl}</div>
                        <div className="link-meta">
                          <span className="click-count">{link.clickCount || 0} clicks</span>
                          <span className="created-date">{formatDate(link.createdAt)}</span>
                        </div>
                      </div>

                      <div className="link-actions">
                        <button
                          className="quick-action"
                          onClick={() => navigator.clipboard.writeText(shortUrl)}
                          title="Copy URL"
                        >
                          📋
                        </button>
                        <button
                          className="quick-action"
                          onClick={() => navigate(`/admin/links/${link.id}/edit`)}
                          title="Edit link"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🚀</div>
                <h4>Ready to create your first link?</h4>
                <p>Transform long URLs into short, trackable links</p>
                <button
                  className="create-first-btn"
                  onClick={() => navigate('/admin/links')}
                >
                  Create Your First Link
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Insights */}
        <div className="content-section">
          <div className="section-header">
            <h3>💡 Quick Insights</h3>
            <button
              className="view-all-btn"
              onClick={() => navigate('/admin/analytics')}
            >
              View Analytics
            </button>
          </div>

          <div className="insights-content">
            <div className="insight-card">
              <div className="insight-icon">📈</div>
              <div className="insight-content">
                <h4>Performance Tip</h4>
                <p>Links with descriptive titles get 23% more clicks than random short codes.</p>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-icon">🎯</div>
              <div className="insight-content">
                <h4>Best Performing</h4>
                <p>Links with custom aliases perform 23% better than random short codes.</p>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-icon">📱</div>
              <div className="insight-content">
                <h4>Mobile Traffic</h4>
                <p>68% of your clicks come from mobile devices. Make sure destination pages are mobile-friendly.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modern-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .dashboard-hero {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 20px;
          padding: 40px;
          margin-bottom: 32px;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .dashboard-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 30px 30px;
          animation: float 20s linear infinite;
        }

        @keyframes float {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-30px, -30px); }
        }

        .hero-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .welcome-section h1 {
          margin: 0 0 8px 0;
          font-size: 32px;
          font-weight: 700;
        }

        .welcome-section p {
          margin: 0;
          font-size: 16px;
          opacity: 0.9;
        }

        .hero-actions {
          display: flex;
          gap: 16px;
        }

        .primary-action,
        .secondary-action {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          backdrop-filter: blur(10px);
        }

        .primary-action:hover,
        .secondary-action:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        .action-icon {
          font-size: 16px;
        }

        .stats-section {
          margin-bottom: 32px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
        }

        .stat-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
        }

        .stat-card.primary::before { background: #667eea; }
        .stat-card.success::before { background: #10b981; }
        .stat-card.warning::before { background: #f59e0b; }
        .stat-card.info::before { background: #3b82f6; }

        .stat-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }

        .stat-icon {
          font-size: 32px;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f9fafb;
          border-radius: 12px;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .stat-change {
          font-size: 12px;
          color: #10b981;
          font-weight: 500;
        }

        .dashboard-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 32px;
        }

        .content-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .section-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .view-all-btn {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .view-all-btn:hover {
          background: #e5e7eb;
          transform: translateY(-1px);
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #f3f4f6;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state h4 {
          margin: 0 0 8px 0;
          color: #1a1a1a;
          font-size: 18px;
        }

        .empty-state p {
          margin: 0 0 20px 0;
          color: #6b7280;
        }

        .create-first-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .create-first-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        .links-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .recent-link-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .recent-link-item:hover {
          background: #f3f4f6;
          transform: translateY(-1px);
        }

        .link-thumbnail {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          overflow: hidden;
          background: #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .link-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumbnail-placeholder {
          font-size: 20px;
          color: #6b7280;
        }

        .link-info {
          flex: 1;
          min-width: 0;
        }

        .link-title {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .link-url {
          font-size: 12px;
          color: #667eea;
          font-family: 'Monaco', 'Menlo', monospace;
          margin-bottom: 4px;
        }

        .link-meta {
          display: flex;
          gap: 12px;
          font-size: 11px;
          color: #6b7280;
        }

        .click-count {
          font-weight: 600;
          color: #374151;
        }

        .link-actions {
          display: flex;
          gap: 4px;
        }

        .quick-action {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          padding: 6px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .quick-action:hover {
          background: #f3f4f6;
          transform: scale(1.05);
        }

        .insights-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .insight-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 12px;
        }

        .insight-icon {
          font-size: 24px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border-radius: 8px;
          flex-shrink: 0;
        }

        .insight-content h4 {
          margin: 0 0 6px 0;
          font-size: 14px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .insight-content p {
          margin: 0;
          font-size: 13px;
          color: #6b7280;
          line-height: 1.4;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .hero-content {
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }

          .hero-actions {
            flex-direction: column;
            width: 100%;
          }

          .primary-action,
          .secondary-action {
            width: 100%;
            justify-content: center;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .dashboard-content {
            grid-template-columns: 1fr;
            gap: 20px;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .dashboard-hero {
            padding: 30px 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernDashboard;